import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle,
  Copy,
  Database,
  DownloadSimple,
  FloppyDisk,
  Funnel,
  Play,
  Plus,
  SortAscending,
  Trash,
  WarningCircle,
} from "@phosphor-icons/react";

const OPERATOR_LABELS = {
  eq: "is",
  ne: "is not",
  contains: "contains",
  starts_with: "starts with",
  gte: "is at least",
  lte: "is at most",
  is_empty: "is empty",
};

const emptyConfig = (area) => ({
  area: area?.key || "students",
  workspace_scope: "all",
  workspace_ids: [],
  columns: (area?.fields || []).slice(0, 5).map((field) => field.key),
  filters: [],
  sort_by: "",
  sort_direction: "asc",
  group_by: "",
});

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const ERROR_ALERT_CLASS = [
  "flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3",
  "text-sm text-rose-900",
].join(" ");

const SUCCESS_ALERT_CLASS = [
  "mb-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3",
  "text-sm text-emerald-900",
].join(" ");

const LIMIT_ALERT_CLASS = [
  "mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3",
  "text-sm text-amber-900",
].join(" ");

const CENTER_STATE_CLASS = [
  "min-h-80 grid place-items-center text-center text-sm text-muted-foreground",
].join(" ");

export default function DatabaseQueryTab() {
  const [catalog, setCatalog] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);
  const [config, setConfig] = useState(emptyConfig());
  const [result, setResult] = useState(null);
  const [reportName, setReportName] = useState("");
  const [description, setDescription] = useState("");
  const [activeQueryId, setActiveQueryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [exportedAt, setExportedAt] = useState(null);

  const area = useMemo(
    () => catalog?.areas?.find((item) => item.key === config.area) || null,
    [catalog, config.area],
  );
  const fields = area?.fields || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogResponse, workspaceResponse, savedResponse] = await Promise.all([
        api.get("/platform/database-queries/catalog"),
        api.get("/platform/database-queries/workspaces"),
        api.get("/platform/database-queries"),
      ]);
      const nextCatalog = catalogResponse.data;
      setCatalog(nextCatalog);
      setWorkspaces(workspaceResponse.data.workspaces || []);
      setSavedQueries(savedResponse.data.queries || []);
      const firstArea = nextCatalog.areas?.[0];
      if (firstArea) {
        setConfig((current) => (
          current.columns.length ? current : emptyConfig(firstArea)
        ));
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateConfig = (patch) => {
    setValidationError("");
    setExportedAt(null);
    setConfig((current) => ({ ...current, ...patch }));
  };

  const changeArea = (areaKey) => {
    const nextArea = catalog?.areas?.find((item) => item.key === areaKey);
    updateConfig({ ...emptyConfig(nextArea), area: areaKey });
    setResult(null);
  };

  const toggleColumn = (fieldKey, checked) => {
    const columns = checked
      ? [...config.columns, fieldKey]
      : config.columns.filter((key) => key !== fieldKey);
    updateConfig({ columns });
  };

  const toggleWorkspace = (workspaceId, checked) => {
    const ids = checked
      ? [...config.workspace_ids, workspaceId]
      : config.workspace_ids.filter((id) => id !== workspaceId);
    updateConfig({ workspace_ids: ids });
  };

  const addFilter = () => {
    if (!fields[0]) return;
    updateConfig({
      filters: [...config.filters, { field: fields[0].key, operator: "eq", value: "" }],
    });
  };

  const updateFilter = (index, patch) => {
    updateConfig({
      filters: config.filters.map((filter, currentIndex) => (
        currentIndex === index ? { ...filter, ...patch } : filter
      )),
    });
  };

  const removeFilter = (index) => {
    updateConfig({ filters: config.filters.filter((_, currentIndex) => currentIndex !== index) });
  };

  const validateBeforeRun = () => {
    if (!config.columns.length) {
      setValidationError("Select at least one field before running the report.");
      return false;
    }
    if (config.workspace_scope === "selected" && !config.workspace_ids.length) {
      setValidationError("Choose one or more workspaces, or report across all workspaces.");
      return false;
    }
    const incomplete = config.filters.some(
      (filter) => filter.operator !== "is_empty" && !String(filter.value || "").trim(),
    );
    if (incomplete) {
      setValidationError("Enter a value for every active filter before running the report.");
      return false;
    }
    return true;
  };

  const runReport = async () => {
    if (!validateBeforeRun()) return;
    setRunning(true);
    setValidationError("");
    try {
      const { data } = await api.post("/platform/database-queries/run", config);
      setResult(data);
      const suffix = data.shown_count === 1 ? "" : "s";
      toast.success(`Report ready: ${data.shown_count} row${suffix} shown.`);
    } catch (error) {
      const message = formatApiError(error);
      setValidationError(message);
      toast.error(message);
    } finally {
      setRunning(false);
    }
  };

  const exportReport = async () => {
    if (!validateBeforeRun()) return;
    setExporting(true);
    try {
      const response = await api.post("/platform/database-queries/export", config, {
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || "therapyflow-report.xlsx";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportedAt(new Date().toISOString());
      toast.success("Excel workbook downloaded.");
    } catch (error) {
      const message = formatApiError(error);
      setValidationError(message);
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const saveReport = async () => {
    if (!reportName.trim()) {
      setValidationError("Give this saved report a name first.");
      return;
    }
    if (!validateBeforeRun()) return;
    setSaving(true);
    try {
      const payload = { name: reportName, description, config };
      const response = activeQueryId
        ? await api.put(`/platform/database-queries/${activeQueryId}`, payload)
        : await api.post("/platform/database-queries", payload);
      const saved = response.data.query;
      setActiveQueryId(saved.id);
      setSavedQueries((current) => [
        saved,
        ...current.filter((query) => query.id !== saved.id),
      ]);
      toast.success(activeQueryId ? "Saved report updated." : "Report saved for later.");
    } catch (error) {
      const message = formatApiError(error);
      setValidationError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openSaved = (query) => {
    setConfig({
      ...query.config,
      sort_by: query.config.sort_by || "",
      group_by: query.config.group_by || "",
    });
    setReportName(query.name);
    setDescription(query.description || "");
    setActiveQueryId(query.id);
    setResult(null);
    setValidationError("");
    toast(`Opened “${query.name}”.`, { icon: "📊" });
  };

  const duplicateSaved = (query) => {
    setConfig({
      ...query.config,
      sort_by: query.config.sort_by || "",
      group_by: query.config.group_by || "",
    });
    setReportName(`${query.name} copy`);
    setDescription(query.description || "");
    setActiveQueryId(null);
    setResult(null);
    setValidationError("");
  };

  const deleteSaved = async (query) => {
    if (!window.confirm(`Delete saved report “${query.name}”?`)) return;
    try {
      await api.delete(`/platform/database-queries/${query.id}`);
      setSavedQueries((current) => current.filter((item) => item.id !== query.id));
      if (activeQueryId === query.id) {
        setActiveQueryId(null);
        setReportName("");
        setDescription("");
      }
      toast.success("Saved report deleted.");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const startNew = () => {
    setConfig(emptyConfig(area || catalog?.areas?.[0]));
    setReportName("");
    setDescription("");
    setActiveQueryId(null);
    setResult(null);
    setValidationError("");
    setExportedAt(null);
  };

  if (loading || !catalog || !area) {
    return (
      <div
        className="min-h-64 flex items-center justify-center text-sm text-muted-foreground"
        data-testid="database-query-loading"
      >
        Loading reporting workspace…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="database-query-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div
            className="overline flex items-center gap-1.5"
            data-testid="database-query-eyebrow"
          >
            <Database size={12} weight="duotone" /> Cross-workspace reporting
          </div>
          <h2 className="font-display text-2xl font-bold mt-1">Database Query</h2>
          <p
            className="text-sm text-muted-foreground mt-1 max-w-3xl"
            data-testid="database-query-summary"
          >
            Build safe, reusable reports across approved operational, financial,
            clinical, and workforce data.
          </p>
        </div>
        <Button variant="outline" onClick={startNew} data-testid="database-query-new-button">
          <Plus size={15} className="mr-1.5" /> New report
        </Button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(360px,0.85fr)_minmax(0,1.35fr)]">
        <div className="space-y-4 min-w-0">
          <Card data-testid="database-query-builder">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Funnel size={16} weight="duotone" /> Report setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="database-query-area">Data area</Label>
                <Select value={config.area} onValueChange={changeArea}>
                  <SelectTrigger id="database-query-area" data-testid="database-query-area-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.areas.map((item) => (
                      <SelectItem
                        key={item.key}
                        value={item.key}
                        data-testid={`database-query-area-${item.key}`}
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Workspace scope</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["all", "selected"].map((scope) => (
                    <Button
                      key={scope}
                      type="button"
                      variant={config.workspace_scope === scope ? "default" : "outline"}
                      onClick={() => updateConfig({ workspace_scope: scope })}
                      data-testid={`database-query-scope-${scope}`}
                    >
                      {scope === "all" ? "All workspaces" : "Selected"}
                    </Button>
                  ))}
                </div>
                {config.workspace_scope === "selected" && (
                  <div
                    className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-2"
                    data-testid="database-query-workspace-list"
                  >
                    {workspaces.map((workspace) => {
                      const checked = config.workspace_ids.includes(workspace.id);
                      return (
                        <label
                          key={workspace.id}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              toggleWorkspace(workspace.id, Boolean(value));
                            }}
                            data-testid={`database-query-workspace-${workspace.id}`}
                          />
                          <span className="min-w-0 truncate">{workspace.name}</span>
                          <span className="text-xs text-muted-foreground">/{workspace.slug}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Fields</Label>
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                  data-testid="database-query-fields"
                >
                  {fields.map((field) => {
                    const checked = config.columns.includes(field.key);
                    return (
                      <label
                        key={field.key}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => toggleColumn(field.key, Boolean(value))}
                          data-testid={`database-query-field-${field.key}`}
                        />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Filters</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={addFilter}
                    data-testid="database-query-add-filter"
                  >
                    <Plus size={14} className="mr-1" /> Add filter
                  </Button>
                </div>
                {config.filters.map((filter, index) => (
                  <div
                    className="grid grid-cols-[1fr_1fr_auto] gap-2"
                    key={`${filter.field}-${index}`}
                  >
                    <Select
                      value={filter.field}
                      onValueChange={(value) => updateFilter(index, { field: value })}
                    >
                      <SelectTrigger data-testid={`database-query-filter-field-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {fields.map((field) => (
                          <SelectItem key={field.key} value={field.key}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={filter.operator}
                      onValueChange={(value) => updateFilter(index, { operator: value })}
                    >
                      <SelectTrigger data-testid={`database-query-filter-operator-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(OPERATOR_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFilter(index)}
                      data-testid={`database-query-remove-filter-${index}`}
                      title="Remove filter"
                    >
                      <Trash size={15} />
                    </Button>
                    {filter.operator !== "is_empty" && (
                      <Input
                        className="col-span-3"
                        value={filter.value}
                        onChange={(event) => updateFilter(index, { value: event.target.value })}
                        placeholder="Filter value"
                        data-testid={`database-query-filter-value-${index}`}
                      />
                    )}
                  </div>
                ))}
                {!config.filters.length && (
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="database-query-no-filters"
                  >
                    No filters applied.
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3 border-t pt-4">
                <div className="space-y-1.5">
                  <Label>Sort by</Label>
                  <Select
                    value={config.sort_by || "none"}
                    onValueChange={(value) => updateConfig({
                      sort_by: value === "none" ? "" : value,
                    })}
                  >
                    <SelectTrigger data-testid="database-query-sort-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No custom sort</SelectItem>
                      {fields.map((field) => (
                        <SelectItem key={field.key} value={field.key}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Direction</Label>
                  <Select
                    value={config.sort_direction}
                    onValueChange={(value) => updateConfig({ sort_direction: value })}
                  >
                    <SelectTrigger data-testid="database-query-sort-direction">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Group results</Label>
                  <Select
                    value={config.group_by || "none"}
                    onValueChange={(value) => updateConfig({
                      group_by: value === "none" ? "" : value,
                    })}
                  >
                    <SelectTrigger data-testid="database-query-group-by">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No grouping</SelectItem>
                      {fields.map((field) => (
                        <SelectItem key={field.key} value={field.key}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {validationError && (
                <div
                  className={ERROR_ALERT_CLASS}
                  data-testid="database-query-validation-error"
                >
                  <WarningCircle size={18} className="shrink-0 mt-0.5" weight="fill" />
                  <span>{validationError}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={runReport}
                  disabled={running}
                  data-testid="database-query-run-button"
                >
                  <Play size={15} weight="fill" className="mr-1.5" />
                  {running ? "Running…" : "Run report"}
                </Button>
                <Button
                  variant="outline"
                  onClick={exportReport}
                  disabled={exporting}
                  data-testid="database-query-export-button"
                >
                  <DownloadSimple size={15} className="mr-1.5" />
                  {exporting ? "Exporting…" : "Download Excel"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="database-query-saved-reports">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FloppyDisk size={16} weight="duotone" /> Saved reports
                <Badge variant="outline" data-testid="database-query-saved-count">
                  {savedQueries.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 border-b pb-4">
                <Label htmlFor="database-query-report-name">Report name</Label>
                <Input
                  id="database-query-report-name"
                  value={reportName}
                  onChange={(event) => setReportName(event.target.value)}
                  placeholder="e.g. Monthly attendance overview"
                  data-testid="database-query-report-name"
                />
                <Textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Optional description"
                  rows={2}
                  data-testid="database-query-report-description"
                />
                <Button
                  onClick={saveReport}
                  disabled={saving}
                  className="w-full"
                  data-testid="database-query-save-button"
                >
                  <FloppyDisk size={15} className="mr-1.5" />
                  {saving ? "Saving…" : activeQueryId ? "Update saved report" : "Save report"}
                </Button>
              </div>
              {!savedQueries.length && (
                <p
                  className="text-sm text-muted-foreground py-2"
                  data-testid="database-query-saved-empty"
                >
                  Save a report to reopen, adjust, rerun, or export it later.
                </p>
              )}
              {savedQueries.map((query) => (
                <div
                  key={query.id}
                  className="border-b last:border-0 pb-3 last:pb-0"
                  data-testid={`database-query-saved-${query.id}`}
                >
                  <button
                    type="button"
                    className="text-left w-full group"
                    onClick={() => openSaved(query)}
                    data-testid={`database-query-open-${query.id}`}
                  >
                    <div className="font-medium text-sm group-hover:text-primary">
                      {query.name}
                    </div>
                    <div className="mt-1 flex gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        {catalog.areas.find((item) => item.key === query.config.area)?.label
                          || query.config.area}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {query.config.workspace_scope === "all"
                          ? "All workspaces"
                          : `${query.config.workspace_ids.length} workspaces`}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Updated {formatDate(query.updated_at)}
                    </div>
                  </button>
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => duplicateSaved(query)}
                      data-testid={`database-query-duplicate-${query.id}`}
                    >
                      <Copy size={13} className="mr-1" /> Duplicate
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSaved(query)}
                      className="text-rose-700"
                      data-testid={`database-query-delete-${query.id}`}
                    >
                      <Trash size={13} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card className="min-w-0" data-testid="database-query-results">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <SortAscending size={16} weight="duotone" /> Results preview
                </CardTitle>
                <p
                  className="text-xs text-muted-foreground mt-1"
                  data-testid="database-query-result-summary"
                >
                  {result
                    ? `${result.shown_count} of ${result.record_count} matching row${
                      result.record_count === 1 ? "" : "s"
                    } across ${result.workspace_count} workspace${
                      result.workspace_count === 1 ? "" : "s"
                    }.`
                    : "Run a report to preview matching records here."}
                </p>
              </div>
              {result && (
                <Badge variant="outline" data-testid="database-query-result-count">
                  {result.shown_count} shown
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {exportedAt && (
              <div
                className={SUCCESS_ALERT_CLASS}
                data-testid="database-query-export-success"
              >
                <CheckCircle size={18} weight="fill" />
                Excel export prepared at {formatDate(exportedAt)}.
              </div>
            )}
            {result?.limited && (
              <div
                className={LIMIT_ALERT_CLASS}
                data-testid="database-query-results-limited"
              >
                <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
                <span>
                  Results are constrained to the protected {result.limit.toLocaleString()}-row
                  limit for this action. Refine filters for a smaller report.
                </span>
              </div>
            )}
            {!result && (
              <div
                className={CENTER_STATE_CLASS}
                data-testid="database-query-results-empty-state"
              >
                <div>
                  <Database size={32} className="mx-auto mb-3 opacity-50" weight="duotone" />
                  Choose fields and run a safe report to inspect its results.
                </div>
              </div>
            )}
            {result && result.rows.length === 0 && (
              <div
                className={CENTER_STATE_CLASS}
                data-testid="database-query-no-results"
              >
                No matching records. Adjust the workspace scope or filters and run again.
              </div>
            )}
            {result && result.rows.length > 0 && (
              <div
                className="overflow-x-auto border rounded-md"
                data-testid="database-query-results-table-wrap"
              >
                <Table data-testid="database-query-results-table">
                  <TableHeader>
                    <TableRow>
                      {result.columns.map((column) => (
                        <TableHead key={column.key}>{column.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((row, index) => (
                      <TableRow
                        key={`${index}-${JSON.stringify(row)}`}
                        data-testid={`database-query-result-row-${index}`}
                      >
                        {result.columns.map((column) => (
                          <TableCell key={column.key}>
                            {String(row[column.key] ?? "—")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}